/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import type { Search_ProductHit as SearchProductHit } from '__generated__/graphql-types-do-not-use';
import PropTypes from 'prop-types';
import { compose, getContext } from 'recompose';

import Retracked from 'js/app/retracked';
import type { TrackingContextData } from 'js/lib/retracked';

import { Chip, ChipGroup, TextField } from '@coursera/cds-core';
import { SearchIcon } from '@coursera/cds-icons';
import { StyleSheet, breakPoint } from '@coursera/coursera-ui';
import { useTracker } from '@coursera/event-pulse/react';

import InPageSearchResults from 'bundles/ent-website/components/CourseSearch/InPageSearchResults';
import NumberOfResultsSection from 'bundles/ent-website/components/CourseSearch/NumberOfResultsSection';
import { NUMBER_OF_SEARCH_RESULTS } from 'bundles/ent-website/components/CourseSearch/constants';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { COURSERA_PLUS_POPULAR_DOMAINS_MAPPING } from 'bundles/search-common/constants';

import _t from 'i18n!nls/ent-website';

const styles = StyleSheet.create({
  searchResults: {
    marginTop: '48px',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      marginTop: '24px',
    },
  },
});

type PropsFromCaller = {
  onSearchQueryChange?: (query: string) => void;
  searching: boolean;
  results?: SearchProductHit[];
  _eventData?: TrackingContextData;
};

type Props = PropsFromCaller;

// The domain maps are borrowed from coursera plus but we discovered
// they collapse better in mobile resolutions in a slightly different order.
const rearrangedDomainMaps = [
  COURSERA_PLUS_POPULAR_DOMAINS_MAPPING[0],
  COURSERA_PLUS_POPULAR_DOMAINS_MAPPING[1],
  COURSERA_PLUS_POPULAR_DOMAINS_MAPPING[2],
  COURSERA_PLUS_POPULAR_DOMAINS_MAPPING[4],
  COURSERA_PLUS_POPULAR_DOMAINS_MAPPING[3],
  COURSERA_PLUS_POPULAR_DOMAINS_MAPPING[5],
];

const CourseSearch = ({ onSearchQueryChange, searching, results, _eventData }: Props) => {
  const [currentSearchQuery, setCurrentSearchQuery] = useState<string>('');
  const [currentSearchInput, setCurrentSearchInput] = useState<string>('');
  const tracker = useTracker();
  useEffect(() => {
    if (currentSearchQuery) {
      tracker('perform_search', {
        searchedQuery: currentSearchQuery,
        searchContextDescription: 'teams_landing_search_query_updated',
      });
    }
    Retracked.trackComponent(
      _eventData,
      {
        searchText: currentSearchQuery,
        page: window.location.pathname,
      },
      'teams_landing_search_query_updated',
      'search'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSearchQuery]);

  const updateSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setCurrentSearchInput(query);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentSearchQuery(currentSearchInput);
    onSearchQueryChange?.(currentSearchInput);
  };

  const updateSearchQueryToDomain = (domain: string) => () => {
    const domainAsSearchQuery = COURSERA_PLUS_POPULAR_DOMAINS_MAPPING.find(
      (currentDomain) => currentDomain.id === domain.toLowerCase()
    )?.name;

    setCurrentSearchInput(domainAsSearchQuery || '');
    setCurrentSearchQuery(domainAsSearchQuery || '');
    if (domainAsSearchQuery) {
      onSearchQueryChange?.(domainAsSearchQuery);
    }
  };

  return (
    <TrackedDiv trackingName="teams_landing_search" withVisibilityTracking={true} trackClicks={false}>
      <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <form onSubmit={handleSubmit} css={{ width: '100%', maxWidth: 704 }}>
          <TextField
            fullWidth
            label={_t('Search query')}
            prefix={<SearchIcon />}
            renderLabel={(label) => <div css={{ display: 'none' }}>{label}</div>}
            value={currentSearchInput}
            onChange={updateSearchInput}
            placeholder={_t('e.g. Data Science')}
          />
        </form>
        <div css={{ marginTop: 24, marginBottom: 24 }}>
          <ChipGroup label={_t('Popular')} inlineLabel>
            {rearrangedDomainMaps.map((domain) => (
              <Chip
                key={domain.id}
                onClick={updateSearchQueryToDomain(domain.id)}
                selected={domain.name === currentSearchQuery}
              >
                {domain.name}
              </Chip>
            ))}
          </ChipGroup>
        </div>
      </div>
      {currentSearchQuery && (
        <div
          // @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351
          {...css(styles.searchResults)}
        >
          <div className="tab-contents">
            <NumberOfResultsSection
              currentSearchQuery={currentSearchQuery}
              limitNumberOfResults={NUMBER_OF_SEARCH_RESULTS}
              searching={searching}
              hits={results ?? []}
            />
            <InPageSearchResults
              numberOfResults={NUMBER_OF_SEARCH_RESULTS}
              searching={searching}
              hits={results ?? []}
            />
          </div>
        </div>
      )}
    </TrackedDiv>
  );
};

export default compose<Props, PropsFromCaller>(
  getContext({
    _eventData: PropTypes.object,
  })
)(React.memo(CourseSearch));
